import {useState, useEffect, useCallback} from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UseInstallPromptReturn {
    canInstall: boolean;
    isIOS: boolean;
    isInstalled: boolean;
    showIOSInstructions: boolean;
    setShowIOSInstructions: (show: boolean) => void;
    handleInstallClick: () => void;
}

export function useInstallPrompt(): UseInstallPromptReturn {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showIOSInstructions, setShowIOSInstructions] = useState(false);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches
        || (navigator as any).standalone === true;

    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstallClick = useCallback(() => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choice) => {
                if (choice.outcome === 'accepted') {
                    setDeferredPrompt(null);
                }
            });
        } else if (isIOS) {
            setShowIOSInstructions(true);
        }
    }, [deferredPrompt, isIOS]);

    const canInstall = !isInstalled && (!!deferredPrompt || isIOS);

    return {
        canInstall,
        isIOS,
        isInstalled,
        showIOSInstructions,
        setShowIOSInstructions,
        handleInstallClick,
    };
}
